import React, { useState, useEffect, useMemo, useRef } from "react";
import { MatchState, Player } from "../matchTypes";
import {
  listPlayerProfiles,
  createPlayerProfile,
  PlayerProfile,
} from "../store/supabasePlayerProfiles";
import { supabase } from "../supabaseClient";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { TeamLogoUpload } from "../components/TeamLogoUpload";
import { PlayerEditModal } from "../components/PlayerEditModal";
import { MatchOfficialsEditor } from "../components/MatchOfficials";
import { formatPlayerName } from "../utils/nameFormatter";
import { getPlayerRoleFlags, viceCaptainAvailable } from "../utils/teamRoles";
import {
  Team,
  listTeams,
  getSquadWithPlayers,
  matchFormatToSquadFormat,
} from "../store/teams";

import { updateMatchSchedule } from "../store/supabaseMatches";
interface Props {
  state: MatchState;
  setState: (s: MatchState) => void;
  isPro?: boolean;
  onDiscardMatch?: () => void;
  onSaveSetup?: () => Promise<void>;
  onStartMatch?: () => Promise<void>;
  matchId?: string | null;
  onManageTeams?: () => void;
}

interface ReplacingPlayer {
  playerId: string;
  team: "A" | "B";
  playerData: Player;
}

function newPlayer(name: string, format?: string, profileId?: string, photoUrl?: string, isGuest?: boolean, battingStyle?: string, bowlingStyle?: string): Player {
  return {
    id: crypto.randomUUID(),
    name,
    isOut: false,
    runs: 0,
    balls: 0,
    fours: 0,
    sixes: 0,
    outsCount: format === "INDOOR" ? 0 : undefined,
    profileId,
    photoUrl,
    guest: isGuest,
    battingStyle,
    bowlingStyle,
  };
}

const TeamSheet: React.FC<Props> = ({
  state,
  setState,
  isPro = false,
  onDiscardMatch,
  onSaveSetup,
  onStartMatch,
  matchId,
  onManageTeams,
}) => {
  const [profiles, setProfiles] = useState<PlayerProfile[]>([]);
  const [activeTeam, setActiveTeam] = useState<"A" | "B">("A");
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [showSquadPicker, setShowSquadPicker] = useState<"A" | "B" | null>(null);
  const [squadSize, setSquadSize] = useState(11);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [oversInputValue, setOversInputValue] = useState<string>("");

  const [replacingPlayer, setReplacingPlayer] = useState<ReplacingPlayer | null>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [addingGuestToTeam, setAddingGuestToTeam] = useState<"A" | "B" | null>(null);
  const [guestPlayerName, setGuestPlayerName] = useState("");
  const [guestNickname, setGuestNickname] = useState("");
  const [guestBattingStyle, setGuestBattingStyle] = useState("");
  const [guestBowlingStyle, setGuestBowlingStyle] = useState("");
  const [saveGuestToSquad, setSaveGuestToSquad] = useState(false);

  const [editingPlayer, setEditingPlayer] = useState<{ player: Player; team: "A" | "B" } | null>(null);

  const [showTeamPicker, setShowTeamPicker] = useState<"A" | "B" | null>(null);
  const [savedTeams, setSavedTeams] = useState<Team[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [noSquadTeam, setNoSquadTeam] = useState<Team | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<Record<"A" | "B", string | "">>({ A: "", B: "" });
  const squadFormat = matchFormatToSquadFormat(state.format);

  const [showEditSchedule, setShowEditSchedule] = useState(false);
  const [schedDate, setSchedDate] = useState(state.matchDate || "");
  const [schedTime, setSchedTime] = useState(state.matchTime || "");
  const [schedVenue, setSchedVenue] = useState(state.matchLocation || "");
  const [schedSaving, setSchedSaving] = useState(false);

  useEffect(() => {
    setSchedDate(state.matchDate || "");
    setSchedTime(state.matchTime || "");
    setSchedVenue(state.matchLocation || "");
  }, [state.matchDate, state.matchTime, state.matchLocation]);

  async function handleSaveSchedule() {
    if (!matchId) return;
    setSchedSaving(true);
    try {
      const result = await updateMatchSchedule(matchId, schedDate, schedTime, schedVenue);
      if (result.success) {
        setState({
          ...state,
          matchDate: schedDate,
          matchTime: schedTime,
          matchLocation: schedVenue,
          isScheduled: true,
        });
        showToast("Schedule updated");
        setShowEditSchedule(false);
      } else {
        showToast(result.error || "Failed to update schedule");
      }
    } catch (err) {
      showToast("Error updating schedule");
    } finally {
      setSchedSaving(false);
    }
  }

  async function openTeamPicker(team: "A" | "B") {
    setShowTeamPicker(team);
    setNoSquadTeam(null);
    setTeamsError(null);
    setIsLoadingTeams(true);
    try {
      const teams = await listTeams(squadFormat || undefined);
      setSavedTeams(teams);
    } catch (err: any) {
      setTeamsError(err?.message || "Failed to load teams");
    } finally {
      setIsLoadingTeams(false);
    }
  }

  async function handleLoadTeamSquad(team: Team, side: "A" | "B") {
    if (!squadFormat) return;
    setIsLoadingTeams(true);
    setTeamsError(null);
    setNoSquadTeam(null);
    try {
      const result = await getSquadWithPlayers(team.id, squadFormat);
      if (!result || result.players.length === 0) {
        setNoSquadTeam(team);
        if (side === "A") {
          setState({
            ...state,
            teamAName: team.name || state.teamAName,
            teamALogo: team.logo_url ?? state.teamALogo,
          });
        } else {
          setState({
            ...state,
            teamBName: team.name || state.teamBName,
            teamBLogo: team.logo_url ?? state.teamBLogo,
          });
        }
        setSelectedTeamId((prev) => ({ ...prev, [side]: team.id }));
        showToast(team.name + " selected (no squad players yet)");
        return;
      }
      const otherIds = side === "A" ? selectedBIds : selectedAIds;
      const players = result.players
        .filter((p) => !otherIds.has(p.id))
        .map((p) => newPlayer(p.name, state.format, p.id, p.photo_url ?? undefined, false, p.batting_style, p.bowling_style));
      const idFor = (profileId: string | null) => {
        if (!profileId) return null;
        const found = players.find((pl) => pl.profileId === profileId);
        return found ? found.id : null;
      };
      const captainId = idFor(result.squad.captain_player_id);
      const viceCaptainId = idFor(result.squad.vice_captain_player_id);
      const keeperId = idFor(result.squad.wicketkeeper_player_id);
      if (side === "A") {
        setState({
          ...state,
          teamAName: team.name || state.teamAName,
          teamALogo: team.logo_url ?? state.teamALogo,
          teamAPlayers: players,
          teamACaptainId: captainId,
          teamAViceCaptainId: viceCaptainId,
          teamAKeeperId: keeperId,
        });
      } else {
        setState({
          ...state,
          teamBName: team.name || state.teamBName,
          teamBLogo: team.logo_url ?? state.teamBLogo,
          teamBPlayers: players,
          teamBCaptainId: captainId,
          teamBViceCaptainId: viceCaptainId,
          teamBKeeperId: keeperId,
        });
      }
      setSelectedTeamId((prev) => ({ ...prev, [side]: team.id }));
      showToast(team.name + " squad loaded (" + players.length + " players)");
    } catch (err: any) {
      setTeamsError(err?.message || "Failed to load squad");
    } finally {
      setIsLoadingTeams(false);
    }
  }

  async function handleSelectSavedTeam(teamId: string, side: "A" | "B") {
    if (!teamId) {
      setSelectedTeamId((prev) => ({ ...prev, [side]: "" }));
      return;
    }
    const team = savedTeams.find((t) => t.id === teamId);
    if (!team) return;
    await handleLoadTeamSquad(team, side);
  }

  const teamALogoInputRef = useRef<HTMLInputElement>(null);
  const teamBLogoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function loadProfiles() {
      setIsLoadingProfiles(true);
      try {
        const allProfiles = await listPlayerProfiles();
        setProfiles(Array.isArray(allProfiles) ? allProfiles : []);
      } catch (err: any) {
        // Ignore abort errors (expected during cleanup)
        if (err?.name === 'AbortError' || String(err?.message || '').toLowerCase().includes('abort')) {
          setIsLoadingProfiles(false);
          return;
        }
        console.error("Error loading profiles:", err);
        setProfiles([]);
      } finally {
        setIsLoadingProfiles(false);
      }
    }
    loadProfiles();
  }, []);

  useEffect(() => {
    if (state.oversLimit) {
      setOversInputValue(state.oversLimit.toString());
    }
  }, [state.oversLimit]);

  useEffect(() => {
    let cancelled = false;
    async function loadSavedTeams() {
      if (!squadFormat) return;
      try {
        const teams = await listTeams(squadFormat);
        if (!cancelled) setSavedTeams(teams);
      } catch (err: any) {
        if (!cancelled) console.error("Failed to preload saved teams:", err);
      }
    }
    loadSavedTeams();
    return () => { cancelled = true; };
  }, [squadFormat]);

  const selectedAIds = useMemo(() => {
    return new Set(
      state.teamAPlayers
        .map((p) => p.profileId)
        .filter((id): id is string => !!id)
    );
  }, [state.teamAPlayers]);

  const selectedBIds = useMemo(() => {
    return new Set(
      state.teamBPlayers
        .map((p) => p.profileId)
        .filter((id): id is string => !!id)
    );
  }, [state.teamBPlayers]);

  const availableProfilesForA = useMemo(() => {
    if (!profiles) return [];
    return profiles.filter((p) => !selectedAIds.has(p.id) && !selectedBIds.has(p.id));
  }, [profiles, selectedAIds, selectedBIds]);

  const availableProfilesForB = useMemo(() => {
    if (!profiles) return [];
    return profiles.filter((p) => !selectedAIds.has(p.id) && !selectedBIds.has(p.id));
  }, [profiles, selectedAIds, selectedBIds]);

  const availableProfilesForReplace = useMemo(() => {
    if (!replacingPlayer) return [];

    const team = replacingPlayer.team;
    const otherTeamIds = team === "A" ? selectedBIds : selectedAIds;
    const currentTeamIds = team === "A" ? selectedAIds : selectedBIds;
    const beingReplacedId = replacingPlayer.playerData.profileId;

    return profiles.filter((p) => {
      if (otherTeamIds.has(p.id)) return false;
      if (currentTeamIds.has(p.id) && p.id !== beingReplacedId) return false;
      return true;
    });
  }, [profiles, replacingPlayer, selectedAIds, selectedBIds]);

  const filteredProfiles = useMemo(() => {
    let availableList: PlayerProfile[] = [];

    if (replacingPlayer) {
      availableList = availableProfilesForReplace;
    } else if (showSquadPicker === "A") {
      availableList = availableProfilesForA;
    } else if (showSquadPicker === "B") {
      availableList = availableProfilesForB;
    }

    if (!searchQuery) return availableList;
    const query = searchQuery.toLowerCase();
    return availableList.filter((p) =>
      p.name?.toLowerCase().includes(query)
    );
  }, [showSquadPicker, replacingPlayer, availableProfilesForA, availableProfilesForB, availableProfilesForReplace, searchQuery]);

  function showToast(message: string) {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  }

  function validateAndApplyOvers(value: string): boolean {
    if (!isIndoorMode) return true;

    if (!value.trim()) {
      showToast("Overs required");
      setOversInputValue((state.oversLimit || 5).toString());
      return false;
    }

    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) {
      showToast("Invalid overs value");
      setOversInputValue((state.oversLimit || 5).toString());
      return false;
    }

    if (parsed < 1 || parsed > 18) {
      showToast("Overs must be between 1 and 18 for Indoor matches");
      setOversInputValue((state.oversLimit || 5).toString());
      return false;
    }

    setState({
      ...state,
      oversLimit: parsed,
      innings: state.innings.map(inning => ({
        ...inning,
        oversLimit: parsed,
      })),
    });
    return true;
  }

  function handleOversBlur() {
    if (!canEditOvers) return;
    validateAndApplyOvers(oversInputValue);
  }

  function canReplacePlayer(player: Player): { canReplace: boolean; reason?: string } {
    if (state.status !== "live") {
      return { canReplace: true };
    }

    const hasParticipated = (player.balls || 0) > 0 || (player.runs || 0) > 0;

    if (hasParticipated) {
      return {
        canReplace: false,
        reason: "Cannot replace a player who has already participated. Use Substitute instead.",
      };
    }

    return { canReplace: true };
  }

  function handleReplaceClick(player: Player, team: "A" | "B") {
    const replaceCheck = canReplacePlayer(player);

    if (!replaceCheck.canReplace) {
      showToast(replaceCheck.reason || "Cannot replace this player");
      return;
    }

    setReplacingPlayer({
      playerId: player.id,
      team,
      playerData: player,
    });
    setShowReplaceModal(true);
  }

  function closeReplaceFlow() {
    setReplacingPlayer(null);
    setShowReplaceModal(false);
    setShowGuestForm(false);
    setShowSquadPicker(null);
    setAddingGuestToTeam(null);
    setGuestPlayerName("");
    setGuestNickname("");
    setGuestBattingStyle("");
    setGuestBowlingStyle("");
    setSaveGuestToSquad(false);
    setSearchQuery("");
  }

  function handleAddPlayer(profile: PlayerProfile) {
    if (!showSquadPicker) return;

    const team = showSquadPicker;
    const otherTeam = team === "A" ? "B" : "A";
    const otherPlayers = team === "A" ? state.teamBPlayers : state.teamAPlayers;

    if (otherPlayers.some((p) => p.profileId === profile.id)) {
      showToast(`${profile.name} is already in Team ${otherTeam}`);
      return;
    }

    const newPlayerObj = newPlayer(
      profile.name,
      state.format,
      profile.id,
      profile.photo_url,
      false,
      profile.batting_style,
      profile.bowling_style
    );

    if (team === "A") {
      setState({
        ...state,
        teamAPlayers: [...state.teamAPlayers, newPlayerObj],
      });
    } else {
      setState({
        ...state,
        teamBPlayers: [...state.teamBPlayers, newPlayerObj],
      });
    }

    showToast(`${profile.name} added to Team ${team}`);
  }

  function handleReplaceFromSquad(profile: PlayerProfile) {
    if (!replacingPlayer) return;

    const team = replacingPlayer.team;
    const otherTeam = team === "A" ? "B" : "A";
    const otherPlayers = team === "A" ? state.teamBPlayers : state.teamAPlayers;

    if (otherPlayers.some((p) => p.profileId === profile.id)) {
      showToast(`${profile.name} is already in Team ${otherTeam}`);
      return;
    }

    const newPlayerObj = newPlayer(
      profile.name,
      state.format,
      profile.id,
      profile.photo_url,
      false,
      profile.batting_style,
      profile.bowling_style
    );

    const currentPlayers = team === "A" ? state.teamAPlayers : state.teamBPlayers;
    const updatedPlayers = currentPlayers.map((p) =>
      p.id === replacingPlayer.playerId ? newPlayerObj : p
    );

    if (team === "A") {
      setState({
        ...state,
        teamAPlayers: updatedPlayers,
      });
    } else {
      setState({
        ...state,
        teamBPlayers: updatedPlayers,
      });
    }

    showToast(`${replacingPlayer.playerData.name} replaced with ${profile.name}`);
    closeReplaceFlow();
  }

  function handleReplaceWithGuest() {
    if (!replacingPlayer || !guestPlayerName.trim()) {
      showToast("Please enter a player name");
      return;
    }

    const team = replacingPlayer.team;
    const newPlayerObj = newPlayer(
      guestPlayerName.trim(),
      state.format,
      undefined,
      undefined,
      true
    );

    const currentPlayers = team === "A" ? state.teamAPlayers : state.teamBPlayers;
    const updatedPlayers = currentPlayers.map((p) =>
      p.id === replacingPlayer.playerId ? newPlayerObj : p
    );

    if (team === "A") {
      setState({
        ...state,
        teamAPlayers: updatedPlayers,
      });
    } else {
      setState({
        ...state,
        teamBPlayers: updatedPlayers,
      });
    }

    showToast(`${replacingPlayer.playerData.name} replaced with ${guestPlayerName.trim()}`);
    closeReplaceFlow();
  }

  function handleAddGuestPlayer() {
    if (!addingGuestToTeam || !guestPlayerName.trim()) {
      showToast("Please enter a player name");
      return;
    }

    const team = addingGuestToTeam;
    const newPlayerObj = newPlayer(
      guestPlayerName.trim(),
      state.format,
      undefined,
      undefined,
      true
    );

    if (team === "A") {
      setState({
        ...state,
        teamAPlayers: [...state.teamAPlayers, newPlayerObj],
      });
    } else {
      setState({
        ...state,
        teamBPlayers: [...state.teamBPlayers, newPlayerObj],
      });
    }

    showToast(`${guestPlayerName.trim()} added to Team ${team} as guest`);
    closeReplaceFlow();
  }

  function handleRemovePlayer(playerId: string, team: "A" | "B") {
    const updates: Partial<MatchState> = {};

    if (team === "A") {
      updates.teamAPlayers = state.teamAPlayers.filter((p) => p.id !== playerId);
      if (state.teamACaptainId === playerId) {
        updates.teamACaptainId = null;
      }
      if (state.teamAKeeperId === playerId) {
        updates.teamAKeeperId = null;
      }
      if (state.teamAViceCaptainId === playerId) {
        updates.teamAViceCaptainId = null;
      }
    } else {
      updates.teamBPlayers = state.teamBPlayers.filter((p) => p.id !== playerId);
      if (state.teamBCaptainId === playerId) {
        updates.teamBCaptainId = null;
      }
      if (state.teamBKeeperId === playerId) {
        updates.teamBKeeperId = null;
      }
      if (state.teamBViceCaptainId === playerId) {
        updates.teamBViceCaptainId = null;
      }
    }

    setState({ ...state, ...updates });
    setEditingPlayer(null);
  }

  async function handleAddGuestToSquad(player: Player): Promise<void> {
    if (import.meta.env.DEV) {
      console.log("[AddToSquad] Clicked for player:", player.name);
    }

    try {
      // Validate player data
      if (!player.name?.trim()) {
        showToast("Cannot save player: name is required");
        return;
      }

      if (import.meta.env.DEV) {
        console.log("[AddToSquad] Form values:", {
          name: player.name,
          guest: player.guest,
          profileId: player.profileId
        });
      }

      // Get current authenticated user
      const { data: userData, error: authError } = await supabase.auth.getUser();

      if (import.meta.env.DEV) {
        console.log("[AddToSquad] Current user:", userData?.user?.id);
      }

      if (authError) {
        console.error("[AddToSquad] Auth error:", authError);
        showToast(`Session error: ${authError.message}`);
        return;
      }

      if (!userData?.user) {
        console.error("[AddToSquad] No authenticated user - session expired");
        showToast("Session expired. Please log in again.");
        return;
      }

      // Determine role and batting style (default values, could be enhanced)
      const role: PlayerProfile["role"] = "Batsman";
      const battingStyle: PlayerProfile["batting_style"] = "RHB";

      // Create clean payload with only valid DB columns
      const profileData = {
        name: player.name.trim(),
        role: role,
        batting_style: battingStyle,
        is_guest: false, // Converting from guest to permanent
      };

      if (import.meta.env.DEV) {
        console.log("[AddToSquad] Payload:", profileData);
      }

      // Save to database
      const savedProfile = await createPlayerProfile(profileData);

      if (import.meta.env.DEV) {
        console.log("[AddToSquad] Response:", { savedProfile });
      }

      if (!savedProfile) {
        showToast("Failed to save player profile");
        return;
      }

      // Update the player in the current team to link to profile
      const team = state.teamAPlayers.some(p => p.id === player.id) ? "A" : "B";
      const currentPlayers = team === "A" ? state.teamAPlayers : state.teamBPlayers;

      const updatedPlayers = currentPlayers.map(p =>
        p.id === player.id
          ? {
              ...p,
              guest: false,
              profileId: savedProfile.id,
              photoUrl: savedProfile.photo_url || p.photoUrl
            }
          : p
      );

      if (team === "A") {
        setState({
          ...state,
          teamAPlayers: updatedPlayers,
        });
      } else {
        setState({
          ...state,
          teamBPlayers: updatedPlayers,
        });
      }

      // Re-load player profiles to refresh squad picker
      try {
        const allProfiles = await listPlayerProfiles();
        setProfiles(Array.isArray(allProfiles) ? allProfiles : []);

        if (import.meta.env.DEV) {
          console.log("[AddToSquad] Profiles reloaded:", allProfiles.length);
        }
      } catch (err: any) {
        console.error("[AddToSquad] Error reloading profiles:", err);
      }

      showToast(`✓ ${player.name} saved to squad!`);

      if (import.meta.env.DEV) {
        console.log("[AddToSquad] ✓ SUCCESS - Player saved:", savedProfile.id);
      }

    } catch (err: any) {
      console.error("[AddToSquad ERROR]:", err);
      const errorMsg = err.message || "Failed to save player";
      showToast(`Error: ${errorMsg}`);
    }
  }

  function handleToggleCaptain(playerId: string, team: "A" | "B") {
    const captainIdKey = team === "A" ? "teamACaptainId" : "teamBCaptainId";
    const currentCaptainId = state[captainIdKey];

    if (currentCaptainId === playerId) {
      setState({ ...state, [captainIdKey]: null });
    } else {
      setState({ ...state, [captainIdKey]: playerId });
    }
  }

  function handleToggleWicketkeeper(playerId: string, team: "A" | "B") {
    const keeperIdKey = team === "A" ? "teamAKeeperId" : "teamBKeeperId";
    const currentKeeperId = state[keeperIdKey];

    if (currentKeeperId === playerId) {
      setState({ ...state, [keeperIdKey]: null });
    } else {
      setState({ ...state, [keeperIdKey]: playerId });
    }
  }

  function handleToggleViceCaptain(playerId: string, team: "A" | "B") {
    if (!viceCaptainAvailable(state.format)) return;
    const vcIdKey = team === "A" ? "teamAViceCaptainId" : "teamBViceCaptainId";
    const currentVcId = state[vcIdKey];

    if (currentVcId === playerId) {
      setState({ ...state, [vcIdKey]: null });
    } else {
      setState({ ...state, [vcIdKey]: playerId });
    }
  }

  function handleTeamNameChange(team: "A" | "B", name: string) {
    if (team === "A") {
      setState({ ...state, teamAName: name });
    } else {
      setState({ ...state, teamBName: name });
    }
  }

  function handleLogoUpload(team: "A" | "B", url: string | null) {
    if (team === "A") {
      setState({ ...state, teamALogo: url });
    } else {
      setState({ ...state, teamBLogo: url });
    }
  }

  async function handleSave() {
    if (!onSaveSetup) return;

    if (isIndoorMode && canEditOvers) {
      if (!validateAndApplyOvers(oversInputValue)) {
        return;
      }
    }

    setIsSaving(true);
    try {
      await onSaveSetup();
      showToast("Match setup saved successfully!");
    } catch (err) {
      showToast("Error saving setup. Please try again.");
      console.error("Save error:", err);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleStart() {
    if (!onStartMatch) return;

    if (state.teamAPlayers.length === 0 || state.teamBPlayers.length === 0) {
      showToast("Both teams must have at least one player");
      return;
    }

    if (!state.teamACaptainId || !state.teamBCaptainId) {
      showToast("Both teams must have a captain assigned");
      return;
    }

    if (isIndoorMode && canEditOvers) {
      if (!validateAndApplyOvers(oversInputValue)) {
        return;
      }
    }

    setIsSaving(true);
    try {
      await onStartMatch();
    } catch (err) {
      showToast("Error starting match. Please try again.");
      console.error("Start error:", err);
    } finally {
      setIsSaving(false);
    }
  }

  const isDraft = state.status === "draft" || !state.status;
  const isScheduled = state.status === "scheduled";
  const isLive = state.status === "live";
  const isCompleted = state.status === "completed" || state.status === "Completed";
  const canEdit = isDraft || isScheduled || isLive;
  const isIndoorMode = state.format === "INDOOR";
  const canEditOvers = isIndoorMode && (isDraft || isScheduled) && !isLive && !isCompleted;

  function renderPlayerRow(player: Player, team: "A" | "B") {
    const bgColor = team === "A" ? "bg-green-900/20" : "bg-blue-900/20";
    const borderColor = team === "A" ? "border-green-800/30" : "border-blue-800/30";

    const { isCaptain, isViceCaptain, isWicketkeeper } = getPlayerRoleFlags(player.id, state.format, {
      captainId: team === "A" ? state.teamACaptainId : state.teamBCaptainId,
      viceCaptainId: team === "A" ? state.teamAViceCaptainId : state.teamBViceCaptainId,
      keeperId: team === "A" ? state.teamAKeeperId : state.teamBKeeperId,
    });

    return (
      <div
        key={player.id}
        className={`${bgColor} px-3 py-2 flex items-center gap-2 ${borderColor} border-b last:border-b-0`}
      >
        <PlayerAvatar
          name={player.name}
          photoUrl={player.photoUrl}
          size="sm"
        />

        <div className="flex-1 min-w-0 flex items-center gap-2">
          <div className="font-semibold text-white text-sm sm:text-base truncate">
            {formatPlayerName(player.name)}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {player.guest && (
              <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                Guest
              </span>
            )}
            {isCaptain && (
              <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded font-bold whitespace-nowrap">
                C
              </span>
            )}
            {isViceCaptain && (
              <span className="text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded font-bold whitespace-nowrap">
                VC
              </span>
            )}
            {isWicketkeeper && (
              <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-bold whitespace-nowrap">
                WK
              </span>
            )}
          </div>
        </div>

        {canEdit && (
          <button
            onClick={() => setEditingPlayer({ player, team })}
            className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors shrink-0"
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  const activeTeamName = activeTeam === "A" ? state.teamAName : state.teamBName;
  const activeTeamPlayers = activeTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
  const activeTeamLogo = activeTeam === "A" ? state.teamALogo : state.teamBLogo;
  const activeTeamReady = activeTeamPlayers.length > 0 && Boolean(activeTeam === "A" ? state.teamACaptainId : state.teamBCaptainId);
  const teamAReady = state.teamAPlayers.length > 0 && Boolean(state.teamACaptainId);
  const teamBReady = state.teamBPlayers.length > 0 && Boolean(state.teamBCaptainId);

  return (
    <div className="space-y-4 pb-24 w-full min-w-0">
      <div className="bg-gradient-to-br from-green-950/80 to-slate-950/80 backdrop-blur-sm rounded-2xl p-3 sm:p-5 border border-emerald-500/40 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-400/60 flex items-center justify-center text-2xl">✎</div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-white truncate">Match Setup</h2>
            <p className="text-xs sm:text-sm text-slate-300">Set up your match, teams and match details</p>
          </div>
          <div className="ml-auto hidden sm:flex items-center gap-2 text-xs text-slate-300">
            <span className="rounded-full bg-emerald-500 px-3 py-1.5 text-slate-950 font-bold">1</span><span>Teams</span>
            <span className="text-emerald-400">—</span><span className="rounded-full bg-slate-800 border border-slate-600 px-3 py-1.5">2</span><span>Details</span>
            <span className="text-slate-500">—</span><span className="rounded-full bg-slate-800 border border-slate-600 px-3 py-1.5">3</span><span>Start</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-0">
          {(["A", "B"] as const).map((team) => {
            const isActive = activeTeam === team;
            const ready = team === "A" ? teamAReady : teamBReady;
            const name = team === "A" ? state.teamAName : state.teamBName;
            return (
              <button
                key={team}
                onClick={() => setActiveTeam(team)}
                className={`min-h-12 rounded-t-xl border px-3 py-3 text-base sm:text-lg font-bold transition-colors ${
                  isActive ? "border-emerald-400 bg-emerald-500/20 text-emerald-300" : "border-slate-600 bg-slate-900/70 text-slate-400"
                }`}
              >
                <span className="truncate">{name || `Team ${team}`}</span>
                {ready && <span className="ml-2 text-emerald-300" aria-label={`${name || `Team ${team}`} ready`}>✓</span>}
              </button>
            );
          })}
        </div>

        <div className={`rounded-b-xl border border-t-0 p-3 sm:p-5 ${activeTeam === "A" ? "border-emerald-500/40 bg-green-950/35" : "border-sky-500/40 bg-slate-950/45"}`}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className={`text-sm font-bold ${activeTeam === "A" ? "text-emerald-300" : "text-sky-300"}`}>
              {activeTeamPlayers.length} player{activeTeamPlayers.length === 1 ? "" : "s"}
            </span>
            {activeTeamReady && <span className="text-xs text-emerald-400 font-semibold">Ready ✓</span>}
          </div>

          <div className="flex justify-center mb-3">
            <TeamLogoUpload
              team={activeTeam}
              logoUrl={activeTeamLogo}
              onLogoChange={(url) => handleLogoUpload(activeTeam, url)}
              teamName={activeTeamName}
              disabled={!canEdit}
              hideNameLabel
            />
          </div>

          {canEdit && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="flex flex-col min-w-0">
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Select Team</label>
                <select
                  value={selectedTeamId[activeTeam]}
                  onChange={(e) => handleSelectSavedTeam(e.target.value, activeTeam)}
                  disabled={isLoadingTeams || !squadFormat}
                  className="min-h-11 w-full bg-slate-950/70 border border-slate-600 rounded-lg px-2 py-2 text-white text-sm font-semibold disabled:opacity-50 truncate"
                >
                  <option value="">— Select —</option>
                  {savedTeams.map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col min-w-0">
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Team Name</label>
                <input
                  type="text"
                  value={activeTeamName}
                  onChange={(e) => {
                    handleTeamNameChange(activeTeam, e.target.value);
                    setSelectedTeamId((prev) => ({ ...prev, [activeTeam]: "" }));
                  }}
                  disabled={!canEdit}
                  className="min-h-11 w-full bg-slate-950/50 border border-emerald-500/40 rounded-lg px-3 py-2 text-white text-sm font-semibold disabled:opacity-50"
                  placeholder={`Team ${activeTeam} Name`}
                />
              </div>
            </div>
          )}

          {!canEdit && (
            <div className="mb-3">
              <input
                type="text"
                value={activeTeamName}
                disabled
                className="w-full bg-slate-950/50 border border-emerald-500/40 rounded-lg px-3 py-2.5 text-white text-sm font-semibold opacity-50"
                placeholder={`Team ${activeTeam} Name`}
              />
            </div>
          )}

          {canEdit && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button onClick={() => { setSearchQuery(""); setShowSquadPicker(activeTeam); }} className="min-h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-2 py-2 rounded-lg text-xs sm:text-sm font-bold transition-colors">＋ Players</button>
              <button onClick={() => { setAddingGuestToTeam(activeTeam); setShowGuestForm(true); }} className="min-h-11 bg-slate-600 hover:bg-slate-500 text-white px-2 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors">＋ Guest</button>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-white text-sm">Players ({activeTeamPlayers.length})</h4>
            {canEdit && <button onClick={() => { setSearchQuery(""); setShowSquadPicker(activeTeam); }} className="min-h-11 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold">＋ Add</button>}
          </div>
          {activeTeamPlayers.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No players added yet</div>
          ) : (
            <div className="rounded-lg border border-slate-700/70 overflow-hidden">
              {activeTeamPlayers.map((player) => renderPlayerRow(player, activeTeam))}
            </div>
          )}
        </div>

        {state.format !== "TEST" && (
          <MatchOfficialsEditor
            officials={state.officials}
            profiles={profiles}
            disabled={!canEdit}
            requireConfirm={isLive}
            onChange={(officials) => setState({ ...state, officials })}
          />
        )}

        <div className="mt-4 bg-slate-950/50 rounded-lg p-4 border border-slate-800/30">
          <h3 className="text-lg font-semibold mb-4 text-slate-300">Match Format</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-400 mb-2">
                  Format
                </label>
                <div className="bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-2 text-white">
                  {state.format === "INDOOR" ? "Indoor Cricket" : state.format === "T20" ? "T20 (20 overs)" : state.format === "ODI" ? "ODI (50 overs)" : "Test"}
                </div>
              </div>

              {isIndoorMode && (
                <div>
                  <label className="block text-sm font-semibold text-slate-400 mb-2">
                    Overs per Innings
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={oversInputValue}
                    onChange={(e) => {
                      if (!canEditOvers) {
                        showToast("Cannot change overs after match has started");
                        return;
                      }
                      setOversInputValue(e.target.value);
                    }}
                    onBlur={handleOversBlur}
                    disabled={!canEditOvers}
                    placeholder="5"
                    className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {!canEditOvers && (
                    <p className="text-xs text-slate-500 mt-1">
                      {isLive || isCompleted ? "Overs are locked after the match starts" : "Overs editing is only available for Indoor mode"}
                    </p>
                  )}
                  {canEditOvers && (
                    <p className="text-xs text-slate-400 mt-1">
                      Indoor mode: 1-18 overs
                    </p>
                  )}
                </div>
              )}

              {!isIndoorMode && (
                <div className="bg-blue-950/30 border border-blue-800/30 rounded-lg px-4 py-3">
                  <p className="text-sm text-blue-300">
                    <span className="font-semibold">ICC Rules:</span> Overs are fixed for {state.format} format
                  </p>
                </div>
              )}
            </div>
          </div>

        {(isDraft || isScheduled) && (
          <div className="mt-4 bg-slate-950/50 rounded-lg p-4 border border-slate-800/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-slate-300">Schedule Details</h3>
              <button
                onClick={() => setShowEditSchedule(!showEditSchedule)}
                className="bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg text-sm font-semibold transition-colors text-white"
              >
                {showEditSchedule ? "Cancel" : "Edit"}
              </button>
            </div>

            {!showEditSchedule ? (
              <div className="space-y-1 text-sm text-slate-400">
                <div><span className="text-slate-300 font-semibold">Date:</span> {state.matchDate || "Not set"}</div>
                <div><span className="text-slate-300 font-semibold">Time:</span> {state.matchTime || "Not set"}</div>
                <div><span className="text-slate-300 font-semibold">Venue:</span> {state.matchLocation || "Not set"}</div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-400 mb-2">Date</label>
                  <input
                    type="date"
                    value={schedDate}
                    onChange={(e) => setSchedDate(e.target.value)}
                    className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-400 mb-2">Time</label>
                  <input
                    type="time"
                    value={schedTime}
                    onChange={(e) => setSchedTime(e.target.value)}
                    className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-400 mb-2">Venue</label>
                  <input
                    type="text"
                    value={schedVenue}
                    onChange={(e) => setSchedVenue(e.target.value)}
                    placeholder="e.g., Central Park Cricket Ground"
                    className="w-full bg-slate-900/30 border border-slate-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <button
                  onClick={handleSaveSchedule}
                  disabled={schedSaving}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-6 py-3 rounded-lg font-semibold transition-colors text-white"
                >
                  {schedSaving ? "Saving..." : "Save Schedule"}
                </button>
              </div>
            )}
          </div>
        )}

        {(isDraft || isScheduled) && (
          <div className="mt-6 space-y-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {isSaving ? "Saving..." : "Save Setup"}
            </button>
            <button
              onClick={handleStart}
              disabled={isSaving}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {isSaving ? "Starting..." : "Start Match"}
            </button>
            {onDiscardMatch && (
              <button
                onClick={onDiscardMatch}
                disabled={isSaving}
                className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Discard Match
              </button>
            )}
          </div>
        )}
      </div>

      {showReplaceModal && replacingPlayer && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
              <h2 className="text-2xl font-bold text-white">
                Replace {replacingPlayer.playerData.name}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <button
                onClick={() => {
                  setShowReplaceModal(false);
                  setShowSquadPicker(replacingPlayer.team);
                  setSearchQuery("");
                }}
                className="w-full bg-green-600 hover:bg-green-700 px-6 py-4 rounded-lg font-semibold transition-colors text-white text-left flex items-center justify-between"
              >
                <div>
                  <div className="font-bold">Choose from Cricket Squad</div>
                  <div className="text-sm text-green-200">Select an existing player</div>
                </div>
                <span className="text-2xl">→</span>
              </button>

              <button
                onClick={() => {
                  setShowReplaceModal(false);
                  setShowGuestForm(true);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 px-6 py-4 rounded-lg font-semibold transition-colors text-white text-left flex items-center justify-between"
              >
                <div>
                  <div className="font-bold">Add Guest Player</div>
                  <div className="text-sm text-blue-200">Create a temporary player</div>
                </div>
                <span className="text-2xl">→</span>
              </button>

              <button
                onClick={closeReplaceFlow}
                className="w-full bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg font-semibold transition-colors text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showGuestForm && (replacingPlayer || addingGuestToTeam) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4">
              <h2 className="text-2xl font-bold text-white">
                {replacingPlayer ? "Replace with Guest Player" : "Add Guest Player"}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Player Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={guestPlayerName}
                  onChange={(e) => setGuestPlayerName(e.target.value)}
                  placeholder="Enter player name"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Nickname <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={guestNickname}
                  onChange={(e) => setGuestNickname(e.target.value)}
                  placeholder="e.g., Big Hitter"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Batting Style <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <select
                  value={guestBattingStyle}
                  onChange={(e) => setGuestBattingStyle(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white"
                >
                  <option value="">Not specified</option>
                  <option value="Right-hand bat">Right-hand bat</option>
                  <option value="Left-hand bat">Left-hand bat</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Bowling Style <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <select
                  value={guestBowlingStyle}
                  onChange={(e) => setGuestBowlingStyle(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white"
                >
                  <option value="">Not specified</option>
                  <option value="Right-arm fast">Right-arm fast</option>
                  <option value="Right-arm medium">Right-arm medium</option>
                  <option value="Right-arm off-break">Right-arm off-break</option>
                  <option value="Right-arm leg-break">Right-arm leg-break</option>
                  <option value="Left-arm fast">Left-arm fast</option>
                  <option value="Left-arm medium">Left-arm medium</option>
                  <option value="Left-arm orthodox">Left-arm orthodox</option>
                  <option value="Left-arm chinaman">Left-arm chinaman</option>
                </select>
              </div>

              <div className="flex items-center gap-3 bg-slate-900 rounded-lg p-3 border border-slate-700">
                <input
                  type="checkbox"
                  id="saveToSquad"
                  checked={saveGuestToSquad}
                  onChange={(e) => setSaveGuestToSquad(e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="saveToSquad" className="text-sm text-slate-300">
                  Also save to Cricket Squad
                </label>
              </div>

              <div className="space-y-2">
                <button
                  onClick={replacingPlayer ? handleReplaceWithGuest : handleAddGuestPlayer}
                  disabled={!guestPlayerName.trim()}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 px-6 py-3 rounded-lg font-semibold transition-colors text-white"
                >
                  {replacingPlayer ? "Replace Player" : "Add to Team"}
                </button>
                <button
                  onClick={closeReplaceFlow}
                  className="w-full bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg font-semibold transition-colors text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSquadPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">
                  {replacingPlayer
                    ? `Replace ${replacingPlayer.playerData.name}`
                    : `Add Player to Team ${showSquadPicker}`}
                </h2>
                <button
                  onClick={closeReplaceFlow}
                  className="text-2xl hover:bg-slate-700 rounded-lg p-2 transition-colors text-white"
                >
                  ×
                </button>
              </div>

              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search players..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
              />
            </div>

            <div className="p-6 space-y-4">
              {isLoadingProfiles ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
                  <p className="text-lg font-semibold text-white">Loading players...</p>
                </div>
              ) : filteredProfiles.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-lg text-white">
                    {searchQuery ? "No players found matching your search" : "No available players"}
                  </p>
                  <p className="text-sm text-slate-400 mt-2">
                    {profiles.length === 0
                      ? "Create player profiles first in the Player Profiles section"
                      : "All players have been assigned to teams"}
                  </p>
                  <button
                    onClick={closeReplaceFlow}
                    className="mt-4 bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg font-semibold text-white"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProfiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="bg-slate-900 rounded-lg p-3 flex items-center gap-3 border border-slate-700"
                    >
                      <PlayerAvatar
                        name={profile.name}
                        photoUrl={profile.photo_url}
                        size="sm"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-white">{profile.name}</div>
                        <div className="text-sm text-slate-400">
                          {profile.role || "Player"}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          replacingPlayer
                            ? handleReplaceFromSquad(profile)
                            : handleAddPlayer(profile)
                        }
                        className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-semibold transition-colors text-white"
                      >
                        {replacingPlayer ? "Replace" : "Add"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editingPlayer && (
        <PlayerEditModal
          player={editingPlayer.player}
          team={editingPlayer.team}
          isCaptain={
            (editingPlayer.team === "A" ? state.teamACaptainId : state.teamBCaptainId) ===
            editingPlayer.player.id
          }
          isViceCaptain={
            (editingPlayer.team === "A" ? state.teamAViceCaptainId : state.teamBViceCaptainId) ===
            editingPlayer.player.id
          }
          showViceCaptain={viceCaptainAvailable(state.format)}
          isWicketkeeper={
            (editingPlayer.team === "A" ? state.teamAKeeperId : state.teamBKeeperId) ===
            editingPlayer.player.id
          }
          onClose={() => setEditingPlayer(null)}
          onToggleCaptain={() => handleToggleCaptain(editingPlayer.player.id, editingPlayer.team)}
          onToggleViceCaptain={() => handleToggleViceCaptain(editingPlayer.player.id, editingPlayer.team)}
          onToggleWicketkeeper={() =>
            handleToggleWicketkeeper(editingPlayer.player.id, editingPlayer.team)
          }
          onReplace={() => {
            handleReplaceClick(editingPlayer.player, editingPlayer.team);
            setEditingPlayer(null);
          }}
          onRemove={() => handleRemovePlayer(editingPlayer.player.id, editingPlayer.team)}
          onAddToSquad={
            editingPlayer.player.guest
              ? async () => {
                  await handleAddGuestToSquad(editingPlayer.player);
                  setEditingPlayer(null);
                }
              : undefined
          }
        />
      )}

      {showTeamPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Load Team {showTeamPicker} Squad</h2>
              <button onClick={() => setShowTeamPicker(null)} className="text-2xl hover:bg-slate-700 rounded-lg p-2 text-white">×</button>
            </div>
            <div className="p-6 space-y-3">
              {!squadFormat ? (
                <div className="text-center py-8 text-slate-300">Saved squads are available for Indoor, T20 and ODI formats only.</div>
              ) : isLoadingTeams ? (
                <div className="text-center py-8 text-slate-300">Loading...</div>
              ) : teamsError ? (
                <div className="text-center py-8">
                  <p className="text-red-300 mb-3">{teamsError}</p>
                  <button onClick={() => openTeamPicker(showTeamPicker)} className="underline text-white">Retry</button>
                </div>
              ) : noSquadTeam ? (
                <div className="text-center py-6">
                  <p className="text-white font-semibold mb-1">No {state.format} squad for {noSquadTeam.name}</p>
                  <p className="text-slate-400 text-sm mb-4">Set up the {state.format} squad for this team to load it here.</p>
                  <div className="flex gap-2">
                    <button onClick={() => setNoSquadTeam(null)} className="flex-1 bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg text-white font-semibold">Back</button>
                    {onManageTeams && (
                      <button onClick={() => { setShowTeamPicker(null); onManageTeams(); }} className="flex-1 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-white font-semibold">Set Up Squad</button>
                    )}
                  </div>
                </div>
              ) : savedTeams.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-white mb-3">You have no saved teams yet.</p>
                  {onManageTeams && (
                    <button onClick={() => { setShowTeamPicker(null); onManageTeams(); }} className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded-lg text-white font-semibold">Create a Team</button>
                  )}
                </div>
              ) : (
                savedTeams.map((team) => (
                  <button key={team.id} onClick={() => handleLoadTeamSquad(team, showTeamPicker)} className="w-full bg-slate-900 hover:bg-slate-700 border border-slate-700 rounded-lg px-4 py-3 text-left text-white font-semibold transition-colors">
                    {team.name}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 pointer-events-none">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-2xl max-w-md pointer-events-auto">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✓</span>
              <span className="font-medium">{toastMessage}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSheet;
